// Transfer gating. Phase D.
//
// Spec §"Transfer gating" (lines 936–1044). The transfer-task manifest
// exists so the engine stops confusing "can do the practiced format"
// with "owns the idea." Reading-pattern mastery requires connected-
// text transfer; math-reasoning mastery requires word-problem transfer.
//
// Adaptation from spec verbatim port:
//   - `requiresConnectedTextTransfer(skillId)` reads `inferMasteryClass`
//     from Phase C (`connectedText` mastery class) instead of spec's
//     prefix matching (`ph.`, `hfw.`). Single source of truth lives
//     in masteryClassInference.ts per §C-Q2.
//   - `requiresWordProblemTransfer(skillId)` similarly uses
//     `inferMasteryClass === "strategyTransfer"`.
//
// transferTasks.json is empty in Phase D (§D-Q1). selectTransferTask
// will return null for every skill; evaluateTransferEvidence will
// return required:true, passed:false. Operational consequence: every
// skill with a transfer-required mastery class is stuck at
// provisionalMastery until expert content populates the manifest.
// Phase F surfaces this gap explicitly in the decision-engine output.

import type {
  AgeBand,
  ChildAttempt,
  DecisionBlockerCode,
  DecisionReasonCode,
  SkillMasteryState,
  TransferEvidence,
  TransferTaskDefinition,
} from "./types";
import {
  canCountTowardIndependentReading,
  canCountTowardMastery,
  canCountTowardReadiness,
} from "./supportModeGating";
import { inferChannel, inferMasteryClass } from "./masteryClassInference";
import { MASTERY_POLICIES } from "./masteryEvidence";
// R3 (RED round 1) — reuse the SAME local-midnight day bucket the content-
// variety shuffle already uses, rather than a second day-math implementation.
// Deliberate cross-layer import (adaptation/ -> lib/engine/): this is the
// one existing pure day-boundary helper in the codebase.
import { localDayEpoch } from "@/lib/engine/seededShuffle";

/** MA-295 — the transfer-requirement capability gate.
 *
 * `evaluateTransferEvidence` used to hardcode `required: true` for every
 * skill, while NOTHING in the live app could ever serve-and-TAG a transfer
 * attempt (`selectTransferTask` has zero live callers; no screen sets
 * `isTransferTask`/`transferTaskId` — grep-verified, tests only; the one
 * launch-safe probe in mathExemplars is served untagged). Consequence:
 * `trueMastery`/`durableMastery` were UNREACHABLE for every child, the
 * mascot was pinned at stage 0, and the decision engine wedged at its
 * priority-5 transfer gate — the dead-gate class the 2026-07-20
 * adaptation-engine memo documented and MA-DEC-0721 resolved.
 *
 * Transfer is now REQUIRED only for skills where a live serve+TAG path
 * exists. That is a CODE capability, not a manifest fact — a skill id in
 * transferTasks.json with no serving surface must NOT flip this back to
 * true (that would re-create the dead gate per-skill for ms.read.06 /
 * ms.math.09, the two skills with authored-but-unservable tasks). MA-303
 * (the serve+TAG slice) registers skills here as their serving path
 * lands, which automatically re-tightens trueMastery for exactly those
 * skills — the designed ratchet.
 *
 * Deliberately a code-level registry (one home: transfer gating owns the
 * transfer requirement) — empty until MA-303.
 *
 * MA-303 (registry-flip slice) — "ms.math.09" is the first id added. Its
 * serve+TAG producer shipped in slice 1 (contentToEngineItem.ts's
 * transferTagsForItem + MathSession.recordAttempt spreading it into
 * buildAttempt), and this slice adds the deterministic force-serve
 * (useEngineQuestions.ts's forceInclude seam, gated by
 * isReadyForTransferForceServe below) so "required" (added here) is never
 * shipped without "reachable" — OPERATOR DECISION D1. */
const TRANSFER_SERVABLE_SKILL_IDS: ReadonlySet<string> = new Set<string>([
  "ms.math.09", // MA-303 registry-flip
]);

export function transferServingPathExists(skillId: string): boolean {
  return TRANSFER_SERVABLE_SKILL_IDS.has(skillId);
}

/** MA-303b (RED round 1, R1) — the registry's ids as a list, for a caller
 *  that needs to iterate the (small, rare) set of registered skills, e.g.
 *  the tree path deciding which skills need the unbounded tagged-transfer
 *  merge. Keeps the registry the ONE static source — no second copy. */
export function transferServableSkillIds(): string[] {
  return [...TRANSFER_SERVABLE_SKILL_IDS];
}

/** Reading-pattern mastery requires connected-text transfer.
 * Spec line 944: "For reading patterns, the required transfer surface
 * is connected text, not another word list." */
export function requiresConnectedTextTransfer(skillId: string): boolean {
  return inferMasteryClass(skillId) === "connectedText";
}

/** Math-reasoning mastery requires word-problem transfer.
 * Spec line 945: "For math concept or reasoning, the required transfer
 * surface is a representation shift and, for reasoning, a word problem." */
export function requiresWordProblemTransfer(skillId: string): boolean {
  return inferMasteryClass(skillId) === "strategyTransfer";
}

/** Select an eligible transfer task for the given skill from the
 * authored manifest. Filters by sourceSkillId, ageBand, launch
 * safety; prefers connectedText target for reading, wordProblem
 * target for math-reasoning. Returns null if no eligible task
 * exists (data gap — Phase D ships with empty transferTasks.json
 * per §D-Q1). */
export function selectTransferTask(
  skillId: string,
  _masteryState: SkillMasteryState, // reserved for future eligibility checks (Phase F)
  transferTasks: TransferTaskDefinition[],
  ageBand: AgeBand,
): TransferTaskDefinition | null {
  const eligible = transferTasks.filter((task) => {
    if (task.sourceSkillId !== skillId) return false;
    if (task.ageBand !== ageBand) return false;
    if (task.launchStatus !== "launch_safe") return false;
    if (task.expert_review_needed) return false;
    return true;
  });

  if (requiresConnectedTextTransfer(skillId)) {
    return (
      eligible.find((task) => task.targetDomain === "decodable_connected_text") ??
      null
    );
  }

  if (requiresWordProblemTransfer(skillId)) {
    return (
      eligible.find((task) => task.targetDomain === "math_word_problems") ?? null
    );
  }

  return eligible[0] ?? null;
}

/** Evaluate transfer evidence from the attempt log. Filters to
 * transfer-task attempts (`isTransferTask === true` OR
 * `transferTaskId !== null`); returns a TransferEvidence shape that
 * Phase F's decision engine consumes alongside the SkillMasteryState. */
export function evaluateTransferEvidence(
  skillId: string,
  transferAttempts: ChildAttempt[],
): TransferEvidence {
  const channel = inferChannel(skillId);

  // Only count attempts that are tagged as transfer tasks (the
  // attempt-log distinguishes practice items from transfer probes
  // via isTransferTask + transferTaskId).
  const relevant = transferAttempts.filter(
    (a) => a.isTransferTask || a.transferTaskId !== null,
  );

  const independentPass = relevant.some(
    (a) => a.correct && canCountTowardMastery(a, channel),
  );
  const readinessOnly =
    !independentPass &&
    relevant.some((a) => a.correct && canCountTowardReadiness(a, channel));

  const requiresConnectedText = requiresConnectedTextTransfer(skillId);
  const requiresWordProblem = requiresWordProblemTransfer(skillId);

  // Channel-specific pass criteria per spec lines 1009-1014.
  const passed = requiresConnectedText
    ? relevant.some(
        (a) =>
          a.correct &&
          a.itemType === "connectedText" &&
          canCountTowardIndependentReading(a),
      )
    : requiresWordProblem
      ? relevant.some(
          (a) =>
            a.correct &&
            a.itemType === "wordProblem" &&
            canCountTowardMastery(a, "mathReasoningMastery"),
        )
      : independentPass;

  const reasonCodes: DecisionReasonCode[] = [];
  const blockerCodes: DecisionBlockerCode[] = [];

  // MA-295: a gate that is not REQUIRED must not emit a blocker/required
  // reason — an unconditioned BLOCK_NO_TRANSFER here would leak into every
  // decision's blockerCodes (and any parent-facing evidence read) for
  // skills where transfer cannot even be served, re-creating the dead-gate
  // signal the required-flag fix removes. `required` is computed once
  // below (transferServingPathExists).
  //
  // MA-303 (registry-flip, OPERATOR DECISION D2 — NO RETROACTIVE DEMOTION):
  // a skill entering TRANSFER_SERVABLE_SKILL_IDS must not instantly demote
  // every child already secure/mastered on it with zero transfer history —
  // that re-creates the exact live-kid-facing regression the 2026-07-26
  // slice-1 decision log deferred this registration to avoid. The
  // requirement binds a child only once they have ACTUALLY BEEN SERVED the
  // probe: `relevant` (above) is already the child's tagged-transfer-
  // attempt history for this skill, so `relevant.length > 0` is exactly
  // "has ≥1 tagged transfer attempt (pass or fail)" — a pure function of
  // this call's own attempt-history argument, no stored flag, no
  // migration, no production write. A never-served child (relevant empty)
  // keeps `required: false` and so keeps whatever trueMastery it already
  // has; a served child's `required` flips true the moment its first
  // tagged attempt (pass OR fail) lands, and `passed` (below) then decides
  // trueMastery honestly from there.
  const required = transferServingPathExists(skillId) && relevant.length > 0;
  if (passed) reasonCodes.push("TRANSFER_PASSED");
  if (required && !passed) blockerCodes.push("BLOCK_NO_TRANSFER");
  if (required && requiresConnectedText && !passed) reasonCodes.push("CONNECTED_TEXT_REQUIRED");
  if (required && requiresWordProblem && !passed) reasonCodes.push("WORD_PROBLEM_TRANSFER_REQUIRED");

  const firstCorrect = relevant.find((a) => a.correct);

  return {
    skillId,
    // MA-295: required only where a live serve+TAG path exists (see
    // transferServingPathExists above). Was a hardcoded `true`, which —
    // with no serving path anywhere — made trueMastery unreachable
    // app-wide and wedged the decision engine at priority 5.
    required,
    requiresConnectedText,
    requiresWordProblem,
    taskIdsTried: relevant
      .map((a) => a.transferTaskId)
      .filter((id): id is string => id !== null),
    passed,
    readinessOnly,
    independentPass,
    passTimestamp: firstCorrect?.timestamp ?? null,
    supportUsedAtPass: firstCorrect?.supportMode ?? [],
    reasonCodes,
    blockerCodes,
  };
}

/** MA-303 (registry-flip, OPERATOR DECISION D3) — force-serve readiness.
 * MA-303b (RED round 1, R3/R4/R7) — cooldown + independence-corrected.
 *
 * Pure, deterministic function of a skill's own (already skill-scoped)
 * attempt history (plus an injected `now`, defaulted for production
 * callers): true exactly when the engine should guarantee the skill's
 * transfer probe a slot in the CHILD'S NEXT session pool.
 *
 * Gates, in order:
 *  1. The skill has a live serve+TAG path at all (registry membership) —
 *     force-serving a skill with no probe content would be a no-op at
 *     best and a dead loop at worst.
 *  2. The child has NOT already passed a tagged transfer attempt on this
 *     skill — once passed, D2's `required` is already satisfiable and
 *     there is nothing left to force.
 *  3. R3 COOLDOWN — after a tagged FAIL, do not force again until AT LEAST
 *     3 LOCAL DAYS have elapsed since that fail's own attempt timestamp
 *     (`seededShuffle.ts`'s `localDayEpoch(now)`, the SAME local-midnight
 *     day-bucket the content-variety shuffle already uses — no second day-
 *     math implementation). Boundary is INCLUSIVE:
 *     `localDayEpoch(now) - localDayEpoch(lastFailTimestamp) >= 3`. RED's
 *     own "10 consecutive daily sessions" sim, reproduced as this slice's
 *     pin, forces on days 3, 6, and 9 of that run (a fail on day 0 cools
 *     down through days 1-2, is eligible again on day 3; a fail ON day 3
 *     restarts the same 3-day cooldown, eligible again on day 6; and so on
 *     — RED asked "what bounds it": nothing caps the total number of
 *     re-offers, by design (D3 says "re-offered in a later session under
 *     the same rule" with no stated ceiling) — "Secure" is the honest
 *     label for a child who keeps failing, per R3's own text.
 *  4. READINESS — "a difficulty-4 transfer item must not ambush a
 *     beginner" (spec). Ready = the skill's OWN provisionalMastery is
 *     already true (computeSkillMasteryState — an EXISTING signal, passed
 *     in by the caller so this function never re-derives mastery), OR the
 *     child has ≥ K recent correct FOCAL (non-transfer-tagged) attempts
 *     that the evidence model itself would count toward mastery.
 *
 *     R4 FIX: the K-arm now reuses `canCountTowardMastery` (the SAME
 *     channel-gated predicate `computeSkillMasteryState`'s own
 *     `countedMasteryAttempts` uses) instead of a bare `.correct` check —
 *     RED found the bare check judged a child "ready" off 8 attempts at
 *     hintLevel H4 or 8 post-IDK-revealed "correct" answers, neither of
 *     which the evidence model would ever count as real independent
 *     evidence anywhere else. `canCountTowardMastery` excludes H4 hints,
 *     post-IDK-supported answers, non-launch_safe/expert-review content —
 *     the exact "independently answered" claim this function's own doc
 *     comment and the PR body were making but the code did not enforce.
 *
 *     K is drawn from the skill's OWN mastery-class policy
 *     (MASTERY_POLICIES[inferMasteryClass(skillId)].minimumAttempts) — the
 *     SAME evidence-count bar computeSkillMasteryState's own
 *     `enoughAttempts` gate uses to decide a skill's independent-practice
 *     history is "enough" to judge (masteryEvidence.ts). For ms.math.09
 *     (masteryClass `directPractice`) K = 6. The "recent" window mirrors
 *     the same policy's `recentWindowSize` (8 for directPractice) so this
 *     reads the same trailing slice computeSkillMasteryState itself scores
 *     — never the child's entire lifetime history (R7 — this window slice
 *     is now pinned by a mutation that specifically distinguishes
 *     window-scoped from lifetime-scoped counting, not just the correct
 *     COUNT the earlier D3-7 case relied on).
 *
 * Session-count discipline ("never twice in one session") is NOT this
 * function's job — it is a pure per-call predicate over attempt history,
 * re-evaluated once per session-content fetch (useEngineQuestions.ts's
 * queryFn, keyed by sessionSeed); the underlying content pool carries the
 * probe as a single authored record, so forcing its PRESENCE in the pool
 * (pullItems' `forceInclude` seam) cannot itself duplicate it within one
 * session's bounded-repetition output.
 */
export function isReadyForTransferForceServe(
  skillId: string,
  skillAttempts: ChildAttempt[],
  provisionalMastery: boolean,
  now: Date = new Date(),
): boolean {
  if (!transferServingPathExists(skillId)) return false;

  const tagged = skillAttempts.filter(
    (a) => a.isTransferTask || a.transferTaskId !== null,
  );
  const alreadyPassed = tagged.some((a) => a.correct);
  if (alreadyPassed) return false;

  // R3 cooldown: every remaining tagged attempt (alreadyPassed is false, so
  // every one is a FAIL) — find the most recent, require >= 3 local days.
  if (tagged.length > 0) {
    const lastFailTimestamp = tagged.reduce(
      (latest, a) => (a.timestamp > latest ? a.timestamp : latest),
      tagged[0].timestamp,
    );
    const daysSinceFail = localDayEpoch(now) - localDayEpoch(new Date(lastFailTimestamp));
    if (daysSinceFail < 3) return false;
  }

  if (provisionalMastery) return true;

  const policy = MASTERY_POLICIES[inferMasteryClass(skillId)];
  const channel = inferChannel(skillId);
  const recentWindow = skillAttempts.slice(-policy.recentWindowSize);
  const focalWindow = recentWindow.filter(
    (a) => !a.isTransferTask && a.transferTaskId === null,
  );
  const countedFocal = focalWindow.filter((a) => canCountTowardMastery(a, channel));
  return countedFocal.length >= policy.minimumAttempts;
}
